/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : NewRequestTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import {
  HandleApiActions,
  labCar,
  userManagementApis,
} from "../../../redux/actions";
import customConsole from "../../../config/customConsole";
import { Box } from "@mui/material";
import {
  EEDetails,
  ModuleAccessTypes,
  ProjectDetails,
  SelectModules,
  SWMDetails,
  VariantDetailsBox,
  VehicleComponentDetails,
  VehicleSystemDetails,
} from "../../../components";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomRadioButtonV3 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV3";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";

function NewRequestTab({
  selectedProjectDetails,
  projectStatus,
  moduleTypes,
  accessTypes,
}) {
  console.log(
    "selectedProjectDetailsselectedProjectDetailsselectedProjectDetails",
    selectedProjectDetails
  );
  const dispatch = useDispatch();

  const [selectedPaBtn, setSelectedPaBtn] = useState(moduleTypes?.VARIANT);
  const [selectedAtBtn, setSelectedAtBtn] = useState(accessTypes?.COOWNER);

  const [variantDetails, setVariantDetails] = useState([]);
  const [variantOptionsList, setVariantOptionsList] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);

  const [zoneOptionsList, setZoneOptionsList] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);

  const [zoneEEDetails, setZoneEEDetails] = useState([]);
  const [eeOptionsList, setEEOptionsList] = useState([]);
  const [selectedEE, setSelectedEE] = useState(null);
  const [selectedEEDetails, setSelectedEEDetails] = useState(null);

  const [eeSwmDetails, setEESwmDetails] = useState([]);
  const [swmOptionsList, setSWMOptionsList] = useState([]);
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [selectedSWMDetails, setSelectedSWMDetails] = useState(null);

  const [vehicleSystemDetails, setVehicleSystemDetails] = useState([]);
  const [vehicleSystemOptionsList, setVehicleSystemOptionsList] = useState([]);
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);
  const [selectedVehicleSystemDetails, setSelectedVehicleSystemDetails] =
    useState(null);

  const [vehicleComponentDetails, setVehicleComponentDetails] = useState([]);
  const [vehicleComponentOptionsList, setVehicleComponentOptionsList] =
    useState([]);
  const [selectedVehicleComponent, setSelectedVehicleComponent] =
    useState(null);
  const [selectedVehicleComponentDetails, setSelectedVehicleComponentDetails] =
    useState(null);

  const [reasonForAccess, setReasonForAccess] = useState("");
  const [accessTypeKey, setAccessTypeKey] = useState(0);

  useEffect(() => {
    setAccessTypeKey((k) => k + 1);
  }, [selectedPaBtn]);

  console.log(selectedPaBtn, "selectedPaBtnselectedPaBtn");

  const getVariantDetails = useCallback(
    async ({ projectId }) => {
      let apiDetails = labCar.get_variant_details;

      customConsole({
        ...apiDetails,
        params: {
          project_id: projectId,
          is_complete_details_required: false,
          is_for_access: true,
        },
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: projectId,
              is_complete_details_required: true,
              is_for_access: true,
            },
          })
        )
      );

      if (resp.error) {
        setVariantDetails([]);
      } else {
        setVariantDetails(resp.data);
      }
    },
    [dispatch]
  );

  const getZoneEEDetails = useCallback(
    async ({ projectId, variantId, zonePosition }) => {
      let apiDetails = labCar.get_ee_components_pa;

      customConsole({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ...(zonePosition && { zone_position: zonePosition }),
        },
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: projectId,
              variant_id: variantId,
              status: true,
              ...(zonePosition && { zone_position: zonePosition }),
            },
          })
        )
      );

      if (resp.error) {
        setZoneEEDetails([]);
      } else {
        setZoneEEDetails(resp.data);
      }
    },
    [dispatch]
  );

  const getEESwmDetails = useCallback(
    async ({ projectId, variantId, eeId }) => {
      let apiDetails = labCar.get_sw_modules_pa;

      customConsole({
        ...apiDetails,
        params: {
          project_id: projectId,
          variant_id: variantId,
          ee_id: eeId,
        },
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: projectId,
              variant_id: variantId,
              ee_id: eeId,
              status: true,
            },
          })
        )
      );

      if (resp.error) {
        setEESwmDetails([]);
      } else {
        setEESwmDetails(resp.data);
      }
    },
    [dispatch]
  );

  const getVehicleSystemsDetails = useCallback(
    async ({ projectId, variantId, zonePosition }) => {
      const apiDetails = labCar.get_vehicle_systems_all;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            show_toast: false,
            params: {
              project_id: Number(projectId),
              variant_id: Number(variantId),
              ...(zonePosition && { zone_position: zonePosition }),
            },
          })
        )
      );
      if (resp.error) {
        setVehicleSystemDetails([]);
      } else {
        setVehicleSystemDetails(resp.data);
      }
    },
    [dispatch]
  );

  const getVehicleComponentsDetails = useCallback(
    async ({ projectId, variantId, vsId }) => {
      const apiDetails = labCar.get_vehicle_components_all;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            show_toast: false,
            params: {
              project_id: projectId,
              variant_id: variantId,
              vs_id: vsId,
            },
          })
        )
      );
      if (resp.error) {
        setVehicleComponentDetails([]);
      } else {
        setVehicleComponentDetails(resp.data);
      }
    },
    [dispatch]
  );

  const requestAccess = async ({
    accessType,
    moduleType,
    projectId,
    variantId,
    eeId,
    swmId,
    vsId,
    vcId,
    zonePosition,
    requestReason,
  }) => {
    let apiDetails = userManagementApis.request_access;

    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            access_type: accessType,
            module_type: moduleType,
            project_id: Number(projectId),
            variant_id: Number(variantId),
            ...(eeId && { ee_id: Number(eeId) }),
            ...(swmId && { swm_id: Number(swmId) }),
            ...(zonePosition && { zone_position: zonePosition }),
            ...(vsId && { vs_id: Number(vsId) }),
            ...(vcId && { vc_id: Number(vcId) }),
            request_reason: requestReason,
          },
          show_toast: true,
        })
      )
    );
  };

  useEffect(() => {
    setSelectedPaBtn(moduleTypes?.VARIANT);
    setVariantOptionsList([]);
    setSelectedVariant(null);
    setSelectedVariantDetails(null);
    setZoneOptionsList([]);
    setSelectedZone(null);
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedProjectDetails, moduleTypes]);

  useEffect(() => {
    setReasonForAccess("");
  }, [
    selectedProjectDetails,
    selectedPaBtn,
    selectedAtBtn,
    selectedVariant,
    selectedZone,
    selectedEE,
    selectedSWM,
    selectedVehicleSystem,
    selectedVehicleComponent,
  ]);

  useEffect(() => {
    setZoneOptionsList([]);
    setSelectedZone(null);
  }, [selectedVariant]);

  useEffect(() => {
    setEEOptionsList([]);
    setSelectedEE(null);
    setSelectedEEDetails(null);
    setVehicleSystemOptionsList([]);
    setSelectedVehicleSystem(null);
    setSelectedVehicleSystemDetails(null);
  }, [selectedVariant, selectedZone]);

  useEffect(() => {
    setSWMOptionsList([]);
    setSelectedSWM(null);
    setSelectedSWMDetails(null);
  }, [selectedVariant, selectedZone, selectedEE]);

  useEffect(() => {
    setVehicleComponentOptionsList([]);
    setSelectedVehicleComponent(null);
    setSelectedVehicleComponentDetails(null);
  }, [selectedVariant, selectedZone, selectedVehicleSystem]);

  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      getVariantDetails({
        projectId: selectedProjectDetails?.project_id,
      });
    }
  }, [selectedProjectDetails, getVariantDetails]);

  useEffect(() => {
    let filteredVariantOptionsList = variantDetails
      ?.map((el) => {
        return {
          value: el?.variant_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));

    console.log(
      "filteredVariantOptionsListfilteredVariantOptionsList",
      filteredVariantOptionsList
    );

    setVariantOptionsList(filteredVariantOptionsList);

    if (filteredVariantOptionsList?.length) {
      setSelectedVariant(filteredVariantOptionsList?.[0]?.value);
    }
  }, [variantDetails]);

  useEffect(() => {
    let filteredVariantDetails = variantDetails?.find(
      (el) =>
        Number.parseInt(el?.variant_id) === Number.parseInt(selectedVariant)
    );
    setSelectedVariantDetails(filteredVariantDetails);
  }, [selectedVariant, variantDetails]);

  useEffect(() => {
    if (
      selectedProjectDetails?.project_id &&
      !selectedProjectDetails?.is_zonal &&
      selectedVariantDetails?.variant_id
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
      });
    }

    let filteredZoneOptionsList = selectedVariantDetails?.zone_details
      ?.map((el) => {
        return {
          value: el?.zone?.position,
          label: el?.zone?.zone_name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setZoneOptionsList(filteredZoneOptionsList);
    if (filteredZoneOptionsList?.length) {
      setSelectedZone(filteredZoneOptionsList?.[0]?.value);
    }
  }, [
    selectedProjectDetails,
    selectedVariantDetails,
    getZoneEEDetails,
    getVehicleSystemsDetails,
  ]);

  useEffect(() => {
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedZone
    ) {
      getZoneEEDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
      getVehicleSystemsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        zonePosition: selectedZone,
      });
    }
  }, [
    selectedZone,
    selectedProjectDetails,
    selectedVariantDetails,
    getZoneEEDetails,
    getVehicleSystemsDetails,
  ]);

  useEffect(() => {
    let filteredEEOptionsList = zoneEEDetails
      ?.map((el) => {
        return {
          value: el?.ee_id,
          label: el?.description,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));

    console.log(
      "filteredEEOptionsListfilteredEEOptionsListfilteredEEOptionsList",
      filteredEEOptionsList
    );
    setEEOptionsList(filteredEEOptionsList);

    if (filteredEEOptionsList?.length) {
      setSelectedEE(filteredEEOptionsList[0]?.value);
    }
  }, [zoneEEDetails]);

  useEffect(() => {
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedEE
    ) {
      getEESwmDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        eeId: selectedEE,
      });
    }

    let filteredSelectedEEDetails = zoneEEDetails?.find(
      (el) => Number.parseInt(el?.ee_id) === Number.parseInt(selectedEE)
    );
    setSelectedEEDetails(filteredSelectedEEDetails);
  }, [
    selectedEE,
    selectedProjectDetails,
    selectedVariantDetails,
    zoneEEDetails,
    getEESwmDetails,
  ]);

  useEffect(() => {
    let filteredSWMOptionsList = eeSwmDetails
      ?.map((el) => {
        return {
          value: el?.swm_id,
          label: el?.name,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setSWMOptionsList(filteredSWMOptionsList);

    if (filteredSWMOptionsList?.length) {
      setSelectedSWM(filteredSWMOptionsList[0]?.value);
    }
  }, [eeSwmDetails]);

  useEffect(() => {
    let filteredEESwmDetails = eeSwmDetails?.find(
      (el) => el?.swm_id === selectedSWM
    );
    setSelectedSWMDetails(filteredEESwmDetails);
  }, [selectedSWM, eeSwmDetails]);

  useEffect(() => {
    let filteredVehicleSystemOptionsList = vehicleSystemDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.assembly_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleSystemOptionsList(filteredVehicleSystemOptionsList);

    if (filteredVehicleSystemOptionsList?.length) {
      setSelectedVehicleSystem(filteredVehicleSystemOptionsList[0]?.value);
    }
  }, [vehicleSystemDetails]);

  useEffect(() => {
    if (
      selectedProjectDetails?.project_id &&
      selectedVariantDetails?.variant_id &&
      selectedVehicleSystem
    ) {
      getVehicleComponentsDetails({
        projectId: selectedProjectDetails?.project_id,
        variantId: selectedVariantDetails?.variant_id,
        vsId: selectedVehicleSystem,
      });
    }

    let filteredSelectedVehicleSystemDetails = vehicleSystemDetails?.find(
      (el) =>
        Number.parseInt(el?.id) === Number.parseInt(selectedVehicleSystem)
    );
    setSelectedVehicleSystemDetails(filteredSelectedVehicleSystemDetails);
  }, [
    selectedVehicleSystem,
    selectedProjectDetails,
    selectedVariantDetails,
    vehicleSystemDetails,
    getVehicleComponentsDetails,
  ]);

  useEffect(() => {
    let filteredVehicleComponentOptionsList = vehicleComponentDetails
      ?.map((el) => {
        return {
          value: el?.id,
          label: el?.component_number,
        };
      })
      ?.sort((a, b) => a.label.localeCompare(b.label));
    setVehicleComponentOptionsList(filteredVehicleComponentOptionsList);

    if (filteredVehicleComponentOptionsList?.length) {
      setSelectedVehicleComponent(
        filteredVehicleComponentOptionsList[0]?.value
      );
    }
  }, [vehicleComponentDetails]);

  useEffect(() => {
    let filteredVehicleComponentDetails = vehicleComponentDetails?.find(
      (el) =>
        Number.parseInt(el?.id) === Number.parseInt(selectedVehicleComponent)
    );
    setSelectedVehicleComponentDetails(filteredVehicleComponentDetails);
  }, [selectedVehicleComponent, vehicleComponentDetails]);

  const getAccessType = () => {
    if (selectedAtBtn === accessTypes?.COOWNER) return accessTypes?.COOWNER;
    if (selectedAtBtn === accessTypes?.EDITOR) return accessTypes?.EDITOR;
    return accessTypes?.VIEWER;
  };

  const getModuleType = () => {
    if (selectedPaBtn === moduleTypes?.PROJECT) return moduleTypes?.PROJECT;
    if (selectedPaBtn === moduleTypes?.VARIANT) return moduleTypes?.VARIANT;
    if (selectedPaBtn === moduleTypes?.ZONE) return moduleTypes?.ZONE;
    if (selectedPaBtn === moduleTypes?.EE) return moduleTypes?.EE;
    if (selectedPaBtn === moduleTypes?.SWM) return moduleTypes?.SWM;
    if (selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM)
      return moduleTypes?.VEHICLE_SYSTEM;
    return moduleTypes?.VEHICLE_COMPONENT;
  };

  const isZoneType =
    selectedPaBtn === moduleTypes?.ZONE ||
    selectedPaBtn === moduleTypes?.EE ||
    selectedPaBtn === moduleTypes?.SWM;

  const isEEType =
    selectedPaBtn === moduleTypes?.EE || selectedPaBtn === moduleTypes?.SWM;

  const isVehicleSystemType =
    selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM ||
    selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT;

  return (
    <Box sx={styles.nrContainer}>
      <Box>
        <ModuleAccessTypes
          selectedProjectDetails={selectedProjectDetails}
          selectedPaBtn={selectedPaBtn}
          setSelectedPaBtn={setSelectedPaBtn}
          moduleTypes={moduleTypes}
          hideProject={true}
        />
      </Box>

      <Box sx={styles.centerPanel}>
        <SelectModules
          selectedProjectDetails={selectedProjectDetails}
          selectedPaBtn={selectedPaBtn}
          moduleTypes={moduleTypes}
          variantOptionsList={variantOptionsList}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant}
          zoneOptionsList={zoneOptionsList}
          selectedZone={selectedZone}
          setSelectedZone={setSelectedZone}
          eeOptionsList={eeOptionsList}
          selectedEE={selectedEE}
          setSelectedEE={setSelectedEE}
          swmOptionsList={swmOptionsList}
          selectedSWM={selectedSWM}
          setSelectedSWM={setSelectedSWM}
          vehicleSystemOptionsList={vehicleSystemOptionsList}
          selectedVehicleSystem={selectedVehicleSystem}
          setSelectedVehicleSystem={setSelectedVehicleSystem}
          vehicleComponentOptionsList={vehicleComponentOptionsList}
          selectedVehicleComponent={selectedVehicleComponent}
          setSelectedVehicleComponent={setSelectedVehicleComponent}
        />

        <Box sx={{ mt: 2 }}>
          <CustomRadioButtonV3
            size="small"
            label="Access Type:"
            data={[
              { value: accessTypes?.COOWNER, label: "Co-Owner" },
              { value: accessTypes?.EDITOR, label: "Editor" },
              { value: accessTypes?.VIEWER, label: "Viewer" },
            ]}
            value={selectedAtBtn}
            onChange={(e) => setSelectedAtBtn(e.target.value)}
            txtFldStyle={styles.customTextFieldN}
            direction="row"
            gap={1}
          />
        </Box>

        {console.log("selectedAtBtnselectedAtBtn", selectedAtBtn)}

        <Box sx={styles.nrATTContainer}>
          <CustomFormTextFieldV2
            size={"small"}
            fldType={"text"}
            placeholder={"Reason for access"}
            type={"text"}
            isMultiline={true}
            minRows={3}
            maxRows={5}
            value={reasonForAccess}
            setValue={setReasonForAccess}
            variant={"outlined"}
            txtFldStyle={styles.customTextFieldN}
            resetTrigger={accessTypeKey}
          />
        </Box>
        <Box sx={styles.nrATbContainer}>
          <CustomButtonV2
            size={"small"}
            btnName={"Send Request"}
            disabled={
              !reasonForAccess ||
              reasonForAccess.length < 5 ||
              (selectedPaBtn === moduleTypes?.PROJECT &&
                !selectedProjectDetails?.project_id) ||
              (selectedPaBtn === moduleTypes?.VARIANT &&
                (!selectedProjectDetails?.project_id || !selectedVariant)) ||
              (selectedPaBtn === moduleTypes?.ZONE &&
                (!selectedProjectDetails?.project_id ||
                  !selectedVariant ||
                  !selectedZone)) ||
              (selectedPaBtn === moduleTypes?.EE &&
                (!selectedProjectDetails?.project_id ||
                  !selectedVariant ||
                  !selectedEEDetails?.ee_id)) ||
              (selectedPaBtn === moduleTypes?.SWM &&
                (!selectedProjectDetails?.project_id ||
                  !selectedVariant ||
                  !selectedEEDetails?.ee_id ||
                  !selectedSWMDetails?.swm_id)) ||
              (selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
                (!selectedProjectDetails?.project_id ||
                  !selectedVariant ||
                  !selectedVehicleSystem)) ||
              (selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
                (!selectedProjectDetails?.project_id ||
                  !selectedVariant ||
                  !selectedVehicleComponent))
            }
            handleOnClick={() =>
              requestAccess({
                accessType: getAccessType(),
                moduleType: getModuleType(),
                projectId: selectedProjectDetails?.project_id,
                variantId: selectedVariantDetails?.variant_id,

                zonePosition: isZoneType && selectedZone,
                eeId: isEEType && selectedEE,
                swmId: selectedPaBtn === moduleTypes?.SWM && selectedSWM,

                vsId: isVehicleSystemType && selectedVehicleSystem,
                vcId:
                  selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
                  selectedVehicleComponent,

                requestReason: reasonForAccess,
              })
            }
          />
        </Box>
      </Box>

      <Box sx={styles.rightPanel}>
        <ProjectDetails
          selectedProjectDetails={selectedProjectDetails}
          projectStatus={projectStatus}
        />

        <VariantDetailsBox selectedVariantDetails={selectedVariantDetails} />

        {selectedPaBtn === moduleTypes?.EE && selectedEE && (
          <EEDetails selectedEEDetails={selectedEEDetails} />
        )}

        {selectedPaBtn === moduleTypes?.SWM && selectedSWM && (
          <SWMDetails selectedSWMDetails={selectedSWMDetails} />
        )}

        {selectedPaBtn === moduleTypes?.VEHICLE_SYSTEM &&
          selectedVehicleSystem && (
            <VehicleSystemDetails
              selectedVehicleSystemDetails={selectedVehicleSystemDetails}
            />
          )}

        {selectedPaBtn === moduleTypes?.VEHICLE_COMPONENT &&
          selectedVehicleComponent && (
            <VehicleComponentDetails
              selectedVehicleComponentDetails={selectedVehicleComponentDetails}
            />
          )}
      </Box>
    </Box>
  );
}

NewRequestTab.propTypes = {
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_zonal: PropTypes.bool,
  }),
  projectStatus: PropTypes.object,
  moduleTypes: PropTypes.shape({
    PROJECT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    VARIANT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ZONE: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    EE: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SWM: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    VEHICLE_SYSTEM: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    VEHICLE_COMPONENT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  accessTypes: PropTypes.shape({
    COOWNER: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    EDITOR: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    VIEWER: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default NewRequestTab;

const styles = {
  nrContainer: {
    display: "flex",
    width: "100%",
    height: "calc(100vh - 120px)",
    overflow: "hidden",
  },
  nrATTContainer: {
    flexGrow: 1,
    mt: 1,
  },
  leftPanel: {
    width: 200,
    minWidth: 200,
    overflowY: "auto",
  },
  centerPanel: {
    flex: 1,
    padding: "0 24px",
    overflowY: "auto",
    ml: 1,
  },
  rightPanel: {
    width: 250,
    minWidth: 250,
    padding: "0 16px",
    ml: 1,
  },
};
